import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Trash2, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { LaunchStats } from "@/components/LaunchStats";
import { ContactInbox } from "@/components/ContactInbox";
import { api, imgUrl, priceLabel, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function ReportCard({ item, reload }) {
  const { listing: l, reports } = item;
  const act = async (kind) => {
    await api.post(`/admin/listings/${l.id}/${kind}`);
    toast.success(kind === "remove" ? "Listing removed" : "Reports dismissed");
    reload();
  };
  return (
    <div data-testid={`admin-report-card-${l.id}`} className="grid gap-5 rounded-2xl bg-[#173742] p-5 md:grid-cols-[160px_1fr_auto]">
      <Link to={`/listing/${l.id}`} className="block aspect-[4/3] overflow-hidden rounded-xl bg-[#0f2a33]">
        {l.images?.[0] && <img src={imgUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}
      </Link>
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/listing/${l.id}`} className="font-display font-bold hover:underline">{l.title}</Link>
          <span className="text-sm text-[#d4f253]">{priceLabel(l)}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${l.status === "hidden" ? "bg-orange-500" : "bg-white/10"}`}>{l.status}</span>
        </div>
        <p className="text-xs text-slate-400">by {l.seller_name} · {l.suburb}, {l.state}</p>
        <ul className="space-y-1.5">
          {reports.map((r) => (
            <li key={r.id} className="rounded-lg bg-[#0f2a33] px-3 py-2 text-xs">
              <span className="font-bold uppercase text-red-300">{r.reason}</span> <span className="text-slate-500">· {r.reporter_name} · {timeAgo(r.created_at)}</span>
              {r.details && <p className="mt-1 text-slate-300">{r.details}</p>}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex gap-2 md:flex-col">
        <button data-testid={`admin-remove-${l.id}`} onClick={() => act("remove")} className="flex items-center justify-center gap-1.5 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-bold hover:bg-red-600"><Trash2 className="h-4 w-4" />Remove</button>
        <button data-testid={`admin-dismiss-${l.id}`} onClick={() => act("dismiss")} className="flex items-center justify-center gap-1.5 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold hover:bg-white/5"><CheckCircle2 className="h-4 w-4" />Keep live</button>
      </div>
    </div>
  );
}

function UserReportCard({ item, reload }) {
  const { user: u, reports } = item;
  const act = async (kind) => {
    await api.post(`/admin/users/${u.id}/${kind}`);
    toast.success(kind === "suspend" ? `${u.name} suspended and listings removed` : "Reports dismissed");
    reload();
  };
  return (
    <div data-testid={`admin-user-report-card-${u.id}`} className="grid gap-5 rounded-2xl bg-[#173742] p-5 md:grid-cols-[1fr_auto]">
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/seller/${u.id}`} className="font-display font-bold hover:underline">{u.name}</Link>
          <span className="text-xs text-slate-400">{u.email}</span>
          {u.suspended && <span className="rounded-full bg-orange-500 px-2.5 py-0.5 text-[10px] font-bold uppercase">suspended</span>}
        </div>
        <p className="text-xs text-slate-400">{u.suburb || "–"}, {u.state} · {u.active_listings} active listings · joined {u.member_since ? new Date(u.member_since).toLocaleDateString("en-AU") : "–"}</p>
        <ul className="space-y-1.5">
          {reports.map((r) => (
            <li key={r.id} className="rounded-lg bg-[#0f2a33] px-3 py-2 text-xs">
              <span className="font-bold uppercase text-red-300">{r.reason.replace("_", " ")}</span> <span className="text-slate-500">· {r.reporter_name} · {timeAgo(r.created_at)}</span>
              {r.details && <p className="mt-1 text-slate-300">{r.details}</p>}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex gap-2 md:flex-col">
        <button data-testid={`admin-suspend-${u.id}`} onClick={() => act("suspend")} className="flex items-center justify-center gap-1.5 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-bold hover:bg-red-600"><Ban className="h-4 w-4" />Suspend</button>
        <button data-testid={`admin-user-dismiss-${u.id}`} onClick={() => act("dismiss")} className="flex items-center justify-center gap-1.5 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold hover:bg-white/5"><CheckCircle2 className="h-4 w-4" />Dismiss</button>
      </div>
    </div>
  );
}

function StatesPanel() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/states").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (s) => {
    if (!s.open && !window.confirm(`Open ${s.name}? ${s.waitlist} waitlisted people will be emailed.`)) return;
    const { data } = await api.post(`/admin/states/${s.id}/${s.open ? "close" : "open"}`);
    toast.success(s.open ? `${s.name} set to Coming soon` : `${s.name} is open — emailing ${data.emailed} people`);
    load();
  };
  if (!rows) return <p className="text-slate-400">Loading…</p>;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {rows.map((s) => (
        <div key={s.id} data-testid={`admin-state-${s.id}`} className="flex items-center gap-4 rounded-2xl bg-[#173742] p-4">
          <div className="flex-1">
            <p className="font-bold">{s.name}</p>
            <p className="text-xs text-slate-400"><span data-testid={`admin-state-waitlist-${s.id}`}>{s.waitlist}</span> on waitlist · {s.open ? <span className="text-[#d4f253]">Open</span> : "Coming soon"}</p>
          </div>
          <Switch data-testid={`admin-state-switch-${s.id}`} checked={s.open} onCheckedChange={() => toggle(s)} />
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [userItems, setUserItems] = useState(null);
  const load = () => api.get("/admin/reports").then((r) => setItems(r.data));
  const loadUsers = () => api.get("/admin/user-reports").then((r) => setUserItems(r.data));
  useEffect(() => { if (user?.role === "admin") { load(); loadUsers(); } }, [user]);

  if (user?.role !== "admin") return <div data-testid="admin-forbidden" className="py-32 text-center text-slate-400">Admins only.</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">Moderation</p>
      <h1 className="mt-2 flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight"><ShieldAlert className="h-8 w-8 text-[#d4f253]" />Reports</h1>
      <LaunchStats />
      <Tabs defaultValue="listings" className="mt-6">
        <TabsList className="h-auto flex-wrap justify-start bg-[#173742]">
          <TabsTrigger value="listings" data-testid="admin-tab-listings">Listings ({items?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users ({userItems?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="states" data-testid="admin-tab-states">States</TabsTrigger>
          <TabsTrigger value="contact" data-testid="admin-tab-contact">Contact messages</TabsTrigger>
        </TabsList>
        <TabsContent value="contact" className="mt-6"><ContactInbox /></TabsContent>
        <TabsContent value="states" className="mt-6 space-y-4">
          <p className="text-sm text-slate-400">Open a state to drop its "Coming soon" banner and email everyone on its waitlist.</p>
          <StatesPanel />
        </TabsContent>
        <TabsContent value="listings" className="mt-6 space-y-4">
          <p className="text-sm text-slate-400">Listings with 3+ reports are hidden automatically until you review them.</p>
          {items === null && <p className="text-slate-400">Loading…</p>}
          {items?.length === 0 && <div data-testid="admin-empty" className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-slate-400">All clear — no open listing reports.</div>}
          {items?.map((it) => <ReportCard key={it.listing.id} item={it} reload={load} />)}
        </TabsContent>
        <TabsContent value="users" className="mt-6 space-y-4">
          <p className="text-sm text-slate-400">Suspending a user blocks their login and removes all their live listings.</p>
          {userItems?.length === 0 && <div data-testid="admin-users-empty" className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-slate-400">All clear — no open user reports.</div>}
          {userItems?.map((it) => <UserReportCard key={it.user.id} item={it} reload={loadUsers} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
